import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trash2, Plus } from 'lucide-react';

import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SaveBar } from '@/components/ui/save-bar';
import { GmapsPicker } from '@/components/gmaps/picker';
import { useBrief, useUpdateBriefSlice } from '@/lib/brief';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/brief/')({
  staticData: { crumb: 'Dane podstawowe' },
  component: BasicsPage,
});

/** Brief slice covered here: company, legal, address, hours, is_24h. */
const BasicsSchema = z.object({
  legal_name: z.string().min(3, 'Min. 3 znaki').max(200),
  nip: z
    .string()
    .regex(/^\d{10}$/u, 'NIP to 10 cyfr')
    .refine(nipChecksum, 'Niepoprawna suma kontrolna NIP')
    .or(z.literal('')),
  regon: z.string().regex(/^(\d{9}|\d{14})$/u, 'REGON to 9 lub 14 cyfr').or(z.literal('')),
  contact_email: z.string().email('Wprowadź poprawny e-mail'),
  contact_phone: z.string().min(7, 'Min. 7 znaków').max(40),
  street: z.string().min(3, 'Min. 3 znaki').max(200),
  city: z.string().min(2, 'Min. 2 znaki').max(100),
  zip: z.string().regex(/^\d{2}-\d{3}$/u, 'Kod pocztowy w formacie NN-NNN'),
  is_24h: z.boolean(),
  hours: z
    .array(
      z.object({
        day: z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']),
        open: z.string().regex(/^\d{2}:\d{2}$/u, 'HH:MM'),
        close: z.string().regex(/^\d{2}:\d{2}$/u, 'HH:MM'),
      })
    )
    .max(7),
});
type BasicsInput = z.infer<typeof BasicsSchema>;

const DAY_LABELS: Record<BasicsInput['hours'][number]['day'], string> = {
  mon: 'Pon',
  tue: 'Wt',
  wed: 'Śr',
  thu: 'Czw',
  fri: 'Pt',
  sat: 'Sob',
  sun: 'Niedz',
};

function BasicsPage() {
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();
  const [savedStatus, setSavedStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');

  const defaults: BasicsInput = React.useMemo(
    () => ({
      legal_name: brief?.legal?.legal_name ?? brief?.company?.name ?? '',
      nip: brief?.legal?.nip ?? '',
      regon: brief?.legal?.regon ?? '',
      contact_email: brief?.company?.contact_email ?? '',
      contact_phone: brief?.company?.phone ?? '',
      street: brief?.address?.street ?? '',
      city: brief?.address?.city ?? '',
      zip: brief?.address?.zip ?? '',
      is_24h: brief?.is_24h ?? false,
      hours: (brief?.hours ?? []) as BasicsInput['hours'],
    }),
    [brief]
  );

  const form = useForm<BasicsInput>({
    resolver: zodResolver(BasicsSchema),
    defaultValues: defaults,
    values: defaults,
  });

  const hours = form.watch('hours');
  const is24h = form.watch('is_24h');

  const onSubmit = async (data: BasicsInput) => {
    setSavedStatus('idle');
    try {
      await update.mutateAsync({
        patch: {
          legal: {
            ...(brief?.legal ?? {}),
            legal_name: data.legal_name,
            ...(data.nip ? { nip: data.nip } : {}),
            ...(data.regon ? { regon: data.regon } : {}),
          },
          company: {
            ...(brief?.company ?? {}),
            name: data.legal_name,
            contact_email: data.contact_email,
            phone: data.contact_phone,
          },
          address: { street: data.street, city: data.city, zip: data.zip },
          is_24h: data.is_24h,
          hours: data.is_24h ? [] : data.hours,
        },
      });
      setSavedStatus('saved');
      form.reset(data);
    } catch {
      setSavedStatus('error');
    }
  };

  const onCancel = () => form.reset(defaults);

  const addHour = (day: BasicsInput['hours'][number]['day']) => {
    const current = form.getValues('hours');
    if (current.some((h) => h.day === day)) return;
    form.setValue('hours', [...current, { day, open: '09:00', close: '17:00' }], {
      shouldDirty: true,
    });
  };
  const removeHour = (idx: number) => {
    const current = form.getValues('hours');
    form.setValue(
      'hours',
      current.filter((_, i) => i !== idx),
      { shouldDirty: true }
    );
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-8">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Dane podstawowe
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Te dane pokazujemy w stopce strony i w sekcji kontaktowej. NIP i adres muszą
          być zgodne z rejestracją w CEIDG / KRS.
        </p>
      </header>

      <section className="space-y-5 rounded-[var(--radius-card)] bg-bg p-6 ring-1 ring-black/5">
        <h2 className="font-display text-base font-semibold text-text">Identyfikacja kliniki</h2>

        <Field
          label="Pełna nazwa kliniki"
          htmlFor="legal_name"
          required
          error={form.formState.errors.legal_name?.message}
        >
          <Input id="legal_name" {...form.register('legal_name')} autoComplete="organization" />
        </Field>

        <div className="grid gap-5 md:grid-cols-2">
          <Field label="NIP" htmlFor="nip" hint="10 cyfr" error={form.formState.errors.nip?.message}>
            <Input id="nip" inputMode="numeric" {...form.register('nip')} autoComplete="off" />
          </Field>
          <Field
            label="REGON"
            htmlFor="regon"
            hint="opcjonalnie"
            error={form.formState.errors.regon?.message}
          >
            <Input id="regon" inputMode="numeric" {...form.register('regon')} autoComplete="off" />
          </Field>
        </div>
      </section>

      <section className="space-y-5 rounded-[var(--radius-card)] bg-bg p-6 ring-1 ring-black/5">
        <h2 className="font-display text-base font-semibold text-text">Kontakt</h2>
        <div className="grid gap-5 md:grid-cols-2">
          <Field
            label="E-mail kontaktowy"
            htmlFor="contact_email"
            required
            error={form.formState.errors.contact_email?.message}
          >
            <Input
              id="contact_email"
              type="email"
              autoComplete="email"
              {...form.register('contact_email')}
            />
          </Field>
          <Field
            label="Telefon"
            htmlFor="contact_phone"
            required
            error={form.formState.errors.contact_phone?.message}
          >
            <Input
              id="contact_phone"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              {...form.register('contact_phone')}
            />
          </Field>
        </div>
      </section>

      <section className="space-y-5 rounded-[var(--radius-card)] bg-bg p-6 ring-1 ring-black/5">
        <h2 className="font-display text-base font-semibold text-text">Adres</h2>
        <Field
          label="Ulica i numer"
          htmlFor="street"
          required
          error={form.formState.errors.street?.message}
        >
          <Input
            id="street"
            placeholder="ul. Przykładowa 12"
            autoComplete="address-line1"
            {...form.register('street')}
          />
        </Field>
        <div className="grid gap-5 md:grid-cols-2">
          <Field
            label="Miasto"
            htmlFor="city"
            required
            error={form.formState.errors.city?.message}
          >
            <Input id="city" autoComplete="address-level2" {...form.register('city')} />
          </Field>
          <Field
            label="Kod pocztowy"
            htmlFor="zip"
            hint="NN-NNN"
            required
            error={form.formState.errors.zip?.message}
          >
            <Input
              id="zip"
              placeholder="00-000"
              autoComplete="postal-code"
              {...form.register('zip')}
            />
          </Field>
        </div>
      </section>

      <section className="space-y-5 rounded-[var(--radius-card)] bg-bg p-6 ring-1 ring-black/5">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-base font-semibold text-text">Godziny otwarcia</h2>
          <label className="inline-flex items-center gap-2 text-sm text-text">
            <input
              type="checkbox"
              {...form.register('is_24h')}
              className="size-4 rounded border-border text-accent focus:ring-accent"
            />
            <span>Klinika otwarta 24/7</span>
          </label>
        </div>

        {is24h ? (
          <p className="text-sm text-text-muted">
            Klinika oznaczona jako otwarta całą dobę. Standardowy harmonogram nie jest pokazywany.
          </p>
        ) : (
          <div>
            <div className="flex flex-wrap gap-1.5">
              {(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const).map((d) => {
                const present = hours.some((h) => h.day === d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => (present ? removeHour(hours.findIndex((h) => h.day === d)) : addHour(d))}
                    className={cn(
                      'rounded-full px-3 py-1 text-xs font-medium ring-1 transition-colors',
                      present
                        ? 'bg-accent/10 text-accent ring-accent/20'
                        : 'bg-bg text-text-muted ring-border hover:bg-surface'
                    )}
                  >
                    {DAY_LABELS[d]}
                  </button>
                );
              })}
            </div>
            <ul role="list" className="mt-5 space-y-3">
              {hours.map((h, idx) => (
                <li
                  key={`${h.day}-${idx}`}
                  className="grid grid-cols-[3rem_minmax(0,1fr)_minmax(0,1fr)_2.5rem] items-center gap-3"
                >
                  <span className="text-sm font-medium text-text">{DAY_LABELS[h.day]}</span>
                  <Input
                    type="time"
                    {...form.register(`hours.${idx}.open`)}
                    aria-label={`Godzina otwarcia (${DAY_LABELS[h.day]})`}
                  />
                  <Input
                    type="time"
                    {...form.register(`hours.${idx}.close`)}
                    aria-label={`Godzina zamknięcia (${DAY_LABELS[h.day]})`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Usuń dzień"
                    onClick={() => removeHour(idx)}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ul>
            {hours.length === 0 ? (
              <p className="mt-4 text-sm text-text-subtle">
                Wybierz dni powyżej, aby dodać godziny.
              </p>
            ) : null}
          </div>
        )}
      </section>

      <GmapsPicker />

      <SaveBar
        saving={form.formState.isSubmitting || update.isPending}
        dirty={form.formState.isDirty}
        status={savedStatus}
        onCancel={onCancel}
      />
    </form>
  );
}

/** PL NIP checksum: weighted sum mod 11 of first 9 digits. */
function nipChecksum(nip: string): boolean {
  if (!/^\d{10}$/.test(nip)) return false;
  const w = [6, 5, 7, 2, 3, 4, 5, 6, 7];
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(nip[i]!, 10) * w[i]!;
  return sum % 11 === parseInt(nip[9]!, 10);
}
